import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ClipboardList, Loader2, X, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  scheduled: "border-[#0A7AFF]/30 text-[#0A7AFF] bg-[#0A7AFF]/8",
  ongoing: "border-[#F59E0B]/30 text-[#D97706] bg-[#F59E0B]/8",
  completed: "border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8",
};

export default function Examinations() {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [hallTickets, setHallTickets] = useState(null);
  const [form, setForm] = useState({ title: "", subject: "", exam_date: "", start_time: "10:00 AM", duration_mins: 180, total_marks: 100, eligible_students: 0, exam_type: "End-Sem" });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/exams`, { withCredentials: true }),
        axios.get(`${API}/exams/stats`, { withCredentials: true }),
      ]);
      setData(d.data); setStats(s.data);
    } catch {} finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/exams`, { ...form, eligible_students: parseInt(form.eligible_students), duration_mins: parseInt(form.duration_mins), total_marks: parseInt(form.total_marks) }, { withCredentials: true });
      setShowModal(false); fetchAll();
    } catch {}
  };

  const fetchHallTickets = async (id) => {
    const { data: ht } = await axios.get(`${API}/exams/${id}/hall-tickets`, { withCredentials: true });
    setHallTickets(ht);
  };

  return (
    <div className="space-y-6" data-testid="exams-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Academic</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Examination Management</h2>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="schedule-exam-btn"
          className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
          <ClipboardList className="w-3.5 h-3.5" /> Schedule Exam
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Exams", value: stats.total || 0, color: "#0A7AFF" },
          { label: "Scheduled", value: stats.scheduled || 0, color: "#8B5CF6" },
          { label: "Ongoing", value: stats.ongoing || 0, color: "#F59E0B" },
          { label: "Completed", value: stats.completed || 0, color: "#22C55E" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1fr 100px 80px 80px 80px 80px" }}>
          {["Subject", "Type", "Date", "Time", "Duration", "Students", "Status"].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
          : data.map((item, i) => (
          <div key={item._id} className="grid gap-0 px-5 py-4 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1fr 100px 80px 80px 80px 80px" }}>
            <div>
              <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{item.subject}</p>
              <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{item.title}</p>
            </div>
            <span className="font-['Syne'] text-[10px] font-bold text-[#7A7468] uppercase">{item.exam_type}</span>
            <span className="font-['DM_Sans'] text-xs text-[#0C0C10]">{item.exam_date}</span>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{item.start_time}</span>
            <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">{item.duration_mins}m</span>
            <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{item.eligible_students}</span>
            <div className="flex items-center gap-1.5">
              <span className={`font-['Syne'] font-bold text-[9px] uppercase px-2 py-1 border ${STATUS_STYLES[item.status]}`}>{item.status}</span>
              {item.status !== "cancelled" && (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => fetchHallTickets(item._id)} data-testid={`hall-tickets-${i}`} className="p-1 text-[#BDBAA8] hover:text-[#0C0C10]">
                    <FileText className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/hall-ticket/${item._id}`, "_blank")} data-testid={`hall-ticket-pdf-${i}`} className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#0A7AFF] border border-[#0A7AFF]/20 px-2 py-1 hover:bg-[#0A7AFF]/8 transition-colors">
                    PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Hall Tickets Modal */}
      <AnimatePresence>
        {hallTickets && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setHallTickets(null)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-lg w-full p-8 max-h-[70vh] overflow-y-auto">
              <div className="flex justify-between mb-5">
                <h3 className="font-['Syne'] font-bold text-base text-[#0C0C10]">Hall Tickets ({hallTickets.length})</h3>
                <button onClick={() => setHallTickets(null)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <div className="space-y-2">
                {hallTickets.slice(0, 10).map((ht, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white border border-[#E2DDD4]">
                    <div>
                      <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{ht.student_name}</p>
                      <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{ht.roll_no}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{ht.seat_no}</p>
                      <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{ht.exam_date}</p>
                    </div>
                  </div>
                ))}
                {hallTickets.length > 10 && <p className="font-['DM_Sans'] text-xs text-[#BDBAA8] text-center">+{hallTickets.length - 10} more</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Schedule Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Schedule Exam</h3>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <form onSubmit={handleCreate} className="space-y-4">
                {[["title", "Exam Title"], ["subject", "Subject"], ["exam_date", "Date"], ["start_time", "Start Time"], ["eligible_students", "Eligible Students"]].map(([f, l]) => (
                  <div key={f}>
                    <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                    <input type={f === "exam_date" ? "date" : f.includes("students") ? "number" : "text"} value={form[f]} onChange={e => setForm(p => ({...p, [f]: e.target.value}))} required
                      className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                  </div>
                ))}
                <div>
                  <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">Type</label>
                  <select value={form.exam_type} onChange={e => setForm(p => ({...p, exam_type: e.target.value}))}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                    {["Mid-Sem", "End-Sem", "Lab", "Supplementary"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors mt-2">Schedule</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
